import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';

/**
 * Whole-number input that keeps what the user types while the input has focus. A valid number is
 * passed on as it is typed; an empty or invalid entry is passed on only when the input loses
 * focus (as null, so the model can fall back to its default). This lets a value be cleared and
 * retyped (40 -> 4 -> empty -> 30) without the model snapping the empty input back mid-edit.
 * @param {object} props - TextField props, plus:
 * @param {number|null|undefined} props.value - Value in the model.
 * @param {function(number|null): void} props.onCommit - New value (null: empty or invalid).
 * @param {number} [props.min] - Smallest value passed on while typing.
 * @param {function(string): string} [props.draftHelper] - Helper text for the current draft (overrides helperText while it returns text).
 * @returns {React.ReactElement}
 */
function DeckNumberField({ value, onCommit, min = 1, draftHelper, helperText, inputProps, onBlur, onFocus, ...rest }) {
  const shown = Number.isFinite(value) ? String(value) : '';
  const [draft, setDraft] = useState(shown);
  const [blurs, setBlurs] = useState(0);
  const focusedRef = useRef(false);

  // Follow the model (undo, another control, or what it kept on blur) while the user is not
  // typing here.
  useEffect(() => {
    if (!focusedRef.current) setDraft(shown);
  }, [shown, blurs]);

  const parse = (text) => {
    if (!/^\s*\d+\s*$/.test(text)) return null;
    const n = Number(text);
    return Number.isFinite(n) && n >= min ? Math.floor(n) : null;
  };

  const draftText = typeof draftHelper === 'function' ? draftHelper(draft) : '';

  return (
    <TextField
      {...rest}
      type="number"
      value={draft}
      helperText={draftText || helperText}
      inputProps={{ step: 1, ...(inputProps || {}), ...(Number.isFinite(min) ? { min } : {}) }}
      onFocus={(event) => {
        focusedRef.current = true;
        onFocus?.(event);
      }}
      onChange={(event) => {
        const text = event.target.value;
        setDraft(text);
        const n = parse(text);
        if (n !== null && n !== value) onCommit(n);
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        if (draft !== shown && parse(draft) === null) onCommit(null);
        // Then show what the model kept (it may clamp to the box capacity or a minimum).
        setBlurs((n) => n + 1);
        onBlur?.(event);
      }}
    />
  );
}

export default DeckNumberField;
